import { useEffect, useMemo, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { NSchema as n, type NostrEvent } from '@nostrify/nostrify';
import { Search as SearchIcon, MessageSquare, UserCircle, BookOpen, Hash } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PostCard } from '@/desktop/components/PostCard';
import { TrendingTags } from '@/desktop/components/TrendingTags';
import { ArticleReader } from '@/desktop/apps/ArticlesApp';
import { useDesktop } from '@/desktop/DesktopContext';
import { genUserName } from '@/lib/genUserName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { buildSearch, DITTO_RELAY } from '@/lib/ditto';

type SearchType = 'notes' | 'profiles' | 'articles' | 'hashtags';

const SEARCH_TABS: { id: SearchType; label: string; icon: typeof MessageSquare }[] = [
  { id: 'notes', label: 'Notes', icon: MessageSquare },
  { id: 'profiles', label: 'People', icon: UserCircle },
  { id: 'articles', label: 'Articles', icon: BookOpen },
  { id: 'hashtags', label: 'Tags', icon: Hash },
];

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function useSearch(kind: number, query: string, sort?: 'hot' | 'top') {
  const { nostr } = useNostr();
  return useQuery<NostrEvent[]>({
    queryKey: ['search', kind, sort ?? '', query],
    queryFn: async ({ signal }) => {
      return nostr.query(
        [{ kinds: [kind], search: buildSearch(query, { sort }), limit: 30 }],
        { relays: [DITTO_RELAY], signal },
      );
    },
    enabled: query.trim().length > 0,
    staleTime: 60000,
  });
}

function ResultsSkeleton() {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-2.5">
          <Skeleton className="w-9 h-9 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyHint({ query }: { query: string }) {
  return (
    <div className="p-8 text-center text-muted-foreground text-sm">
      {query.trim() ? 'No results. Try a different search or tab.' : 'Type to search across Nostr.'}
    </div>
  );
}

function ProfileRow({ event, onOpen }: { event: NostrEvent; onOpen: (pubkey: string) => void }) {
  const metadata = useMemo(() => {
    try { return n.json().pipe(n.metadata()).parse(event.content); }
    catch { return undefined; }
  }, [event.content]);
  const name = metadata?.name ?? metadata?.display_name ?? genUserName(event.pubkey);

  return (
    <button
      onClick={() => onOpen(event.pubkey)}
      className="w-full flex items-center gap-3 px-4 py-2.5 border-b border-border/40 hover:bg-muted/20 transition-colors text-left"
    >
      <Avatar className="w-9 h-9 shrink-0">
        <AvatarImage src={metadata?.picture} alt={name} />
        <AvatarFallback className="text-xs">{name.slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">{name}</p>
        {(metadata?.nip05 || metadata?.about) && (
          <p className="text-[11px] text-muted-foreground truncate">
            {metadata?.nip05?.replace(/^_@/, '') ?? metadata?.about}
          </p>
        )}
      </div>
    </button>
  );
}

function getArticleMeta(event: NostrEvent) {
  const tag = (name: string) => event.tags.find(([t]) => t === name)?.[1];
  return { title: tag('title') ?? '(untitled)', summary: tag('summary') ?? '', image: tag('image') };
}

function ArticleRow({ event, onOpen }: { event: NostrEvent; onOpen: (event: NostrEvent) => void }) {
  const { title, summary, image } = getArticleMeta(event);
  const safeImage = sanitizeUrl(image);
  return (
    <button
      onClick={() => onOpen(event)}
      className="w-full flex gap-3 px-4 py-3 border-b border-border/40 hover:bg-muted/20 transition-colors text-left"
    >
      {safeImage && (
        <img src={safeImage} alt="" className="w-14 h-14 rounded-md object-cover shrink-0 bg-muted" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold line-clamp-1">{title}</p>
        {summary && <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{summary}</p>}
      </div>
    </button>
  );
}

export function SearchApp() {
  const { openThread, openProfile } = useDesktop();
  const [type, setType] = useState<SearchType>('notes');
  const [input, setInput] = useState('');
  const [openArticle, setOpenArticle] = useState<NostrEvent | null>(null);
  const query = useDebounced(input, 300);

  const notes = useSearch(1, type === 'notes' ? query : '', 'top');
  const profiles = useSearch(0, type === 'profiles' ? query : '');
  const articles = useSearch(30023, type === 'articles' ? query : '', 'top');

  if (openArticle) {
    return <ArticleReader event={openArticle} onBack={() => setOpenArticle(null)} />;
  }

  const active = type === 'notes' ? notes : type === 'profiles' ? profiles : articles;
  const showResults = type !== 'hashtags';

  return (
    <div className="flex flex-col h-full">
      {/* Search input */}
      <div className="px-3 py-2 border-b border-border/50 bg-muted/30 shrink-0">
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-background border border-border/60">
          <SearchIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search notes, people, articles…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
      </div>

      {/* Type tabs */}
      <div className="flex items-center gap-0 px-3 py-1.5 border-b border-border/40 bg-muted/10 shrink-0">
        {SEARCH_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                type === t.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-3 h-3" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-auto min-h-0">
        {type === 'hashtags' ? (
          <div className="p-3">
            <p className="text-[11px] text-muted-foreground mb-2">Trending hashtags</p>
            <div className="flex flex-wrap gap-2">
              <TrendingTags
                limit={24}
                onSelect={(tag) => { setInput(`#${tag}`); setType('notes'); }}
                className="flex-wrap"
              />
            </div>
          </div>
        ) : showResults && query.trim() && active.isLoading ? (
          <ResultsSkeleton />
        ) : showResults && active.data && active.data.length > 0 ? (
          type === 'notes' ? (
            active.data.map((event) => (
              <PostCard key={event.id} event={event} onOpenThread={openThread} onOpenProfile={openProfile} />
            ))
          ) : type === 'profiles' ? (
            active.data.map((event) => (
              <ProfileRow key={event.id} event={event} onOpen={openProfile} />
            ))
          ) : (
            active.data.map((event) => (
              <ArticleRow key={event.id} event={event} onOpen={setOpenArticle} />
            ))
          )
        ) : (
          <EmptyHint query={query} />
        )}
      </div>
    </div>
  );
}
